// ============================================================================
// COSTS TAB - Project-level LLM cost tracking via LiteLLM
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, Cpu, TrendingUp, Zap, RefreshCw } from 'lucide-react';
import {
  getProjectCosts,
  LiteLLMProjectUsage,
  formatCost,
  formatTokens,
} from '../../services/llmCostsService';

interface CostsTabProps {
  projectId: string;
  token: string;
}

const COLORS = ['#00c7c4', '#0891b2', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];

export const CostsTab: React.FC<CostsTabProps> = ({ projectId }) => {
  const [data, setData] = useState<LiteLLMProjectUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'7d' | '30d' | 'all'>('30d');

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getProjectCosts(projectId, period);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, [projectId, period]);

  useEffect(() => { fetchCosts(); }, [fetchCosts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <RefreshCw size={20} className="animate-spin mr-2" /> Loading cost data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
    );
  }

  if (!data || data.totalRequests === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
        <DollarSign size={40} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500">No LLM cost data recorded yet.</p>
        <p className="text-xs text-slate-400 mt-1">Costs appear once this project's AI workflows run through LiteLLM.</p>
      </div>
    );
  }

  const modelEntries = Object.entries(data.byModel).sort((a, b) => b[1].spend - a[1].spend);
  const pieData = modelEntries.map(([name, d]) => ({ name, value: d.spend }));

  return (
    <div className="flex flex-col gap-6">
      {/* Period selector + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
          {(['7d', '30d', 'all'] as const).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                period === p
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {p === 'all' ? 'All Time' : p}
            </button>
          ))}
        </div>
        <button
          onClick={fetchCosts}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary transition-colors"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        <SummaryCard icon={DollarSign} label="Total Cost" value={formatCost(data.periodSpend)} />
        <SummaryCard icon={Zap} label="Requests" value={data.totalRequests.toString()} />
        <SummaryCard icon={Cpu} label="Input Tokens" value={formatTokens(data.tokens.prompt)} />
        <SummaryCard icon={TrendingUp} label="Output Tokens" value={formatTokens(data.tokens.completion)} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Daily cost trend */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Daily Cost Trend</h3>
          {data.byDay.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.byDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.substring(5)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} />
                <Tooltip
                  formatter={(v: number | undefined) => formatCost(v ?? 0)}
                  labelFormatter={l => `Date: ${l}`}
                />
                <Line type="monotone" dataKey="spend" stroke="#00c7c4" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-slate-400 text-center py-10">Not enough data for trend</p>
          )}
        </div>

        {/* Model cost breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Cost by Model</h3>
          {pieData.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value">
                    {pieData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number | undefined) => formatCost(v ?? 0)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-col gap-2 text-xs">
                {modelEntries.map(([model, d], i) => (
                  <div key={model} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-slate-600 truncate max-w-[140px]">{model}</span>
                    <span className="text-slate-800 font-medium ml-auto">{formatCost(d.spend)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400 text-center py-10">No model data</p>
          )}
        </div>
      </div>
    </div>
  );
};

function SummaryCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon size={18} className="text-primary" />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-bold text-slate-800">{value}</p>
      </div>
    </div>
  );
}
